/**
 * PitchEditor — éditeur de schémas tactiques de foot.
 *
 * Composant SVG paysage/portrait avec :
 * - Pions joueurs (home doré / away rouge), drag & drop, max 22
 * - Ballon (1 seul max)
 * - Cônes orange
 * - Flèches passe / course / dribble (création par clic-clic, courbe optionnelle)
 * - Sélection + suppression (touche Delete/Backspace ou bouton corbeille)
 * - Undo / redo (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z), stack max 50
 * - Pointer events (souris + tactile)
 * - Mode readOnly (juste affichage, aucune interaction)
 *
 * Props :
 *   initialSchema       PitchSchema initial { tokens, arrows, cones }
 *   initialOrientation  'landscape' | 'portrait' (default 'landscape')
 *   onChange            (schema, orientation) => void  appelé à chaque modif
 *   readOnly            si true, terrain visible mais pas d'interaction
 *   showToolbar         si true (default), toolbar visible (sinon juste terrain)
 *   height              hauteur custom optionnelle (CSS string)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight, Circle, Triangle as ConeIcon, MousePointer2,
  RotateCcw, RotateCw, Trash2, Undo2, Redo2, AlertTriangle,
} from 'lucide-react'
import { T } from '../../theme'

// ─────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────

const FIELD_LANDSCAPE = { w: 1050, h: 680 }
const FIELD_PORTRAIT = { w: 680, h: 1050 }
const MAX_TOKENS = 22
const MAX_HISTORY = 50
const MIN_ARROW_PX = 5            // % distance, en-dessous on annule
const TOKEN_R_PCT = 1.9           // rayon du pion en % du min(w,h) de la viewBox
const BALL_R_PCT = 1.0
const CONE_HALF_PCT = 1.1
const SELECTION_GLOW = '0 0 0 3px rgba(79,142,247,0.55)'

const TOOLS = {
  SELECT:        'select',
  TOKEN_HOME:    'token-home',
  TOKEN_AWAY:    'token-away',
  BALL:          'ball',
  CONE:          'cone',
  ARROW_PASS:    'arrow-pass',
  ARROW_RUN:     'arrow-run',
  ARROW_DRIBBLE: 'arrow-dribble',
}

const ARROW_STYLES = {
  pass:    { color: '#ffffff', dash: 'none',  width: 2.5, label: 'Passe' },
  run:     { color: T.gold,    dash: '6 4',   width: 2.5, label: 'Course' },
  dribble: { color: T.red,     dash: '1.5 3', width: 2.5, label: 'Dribble' },
}

const TEAM_COLOR = { home: T.gold, away: T.red }

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

const clamp = (v, min, max) => Math.max(min, Math.min(max, v))
const newId = () => Date.now() + Math.floor(Math.random() * 100000)
const distPct = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

const emptySchema = () => ({ tokens: [], arrows: [], cones: [] })

function normalizeSchema(s) {
  return {
    tokens: Array.isArray(s?.tokens) ? s.tokens.map(t => ({ ...t })) : [],
    arrows: Array.isArray(s?.arrows) ? s.arrows.map(a => ({
      ...a,
      from: { ...a.from },
      to: { ...a.to },
    })) : [],
    cones: Array.isArray(s?.cones) ? s.cones.map(c => ({ ...c })) : [],
  }
}

function clientToPct(svgEl, clientX, clientY) {
  if (!svgEl) return null
  const pt = svgEl.createSVGPoint()
  pt.x = clientX
  pt.y = clientY
  const ctm = svgEl.getScreenCTM()
  if (!ctm) return null
  const local = pt.matrixTransform(ctm.inverse())
  const vb = svgEl.viewBox.baseVal
  return {
    x: clamp((local.x / vb.width) * 100, 0, 100),
    y: clamp((local.y / vb.height) * 100, 0, 100),
  }
}

// ─────────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────────

function Field({ orientation }) {
  const { w, h } = orientation === 'portrait' ? FIELD_PORTRAIT : FIELD_LANDSCAPE
  const stripeCount = 6
  const stripeW = w / stripeCount
  const isPortrait = orientation === 'portrait'

  // Lignes : on calcule en se basant sur ratios FIFA, viewBox 1050×680 ou 680×1050
  // Pour simplifier, on raisonne en landscape puis on rotate le rendering en portrait.
  const lines = isPortrait
    ? renderLinesPortrait(w, h)
    : renderLinesLandscape(w, h)

  return (
    <g pointerEvents="none">
      {/* Pattern d'herbe alterné — bandes verticales */}
      {Array.from({ length: stripeCount }).map((_, i) => (
        <rect
          key={i}
          x={i * stripeW}
          y={0}
          width={stripeW}
          height={h}
          fill={i % 2 === 0 ? T.pitch : T.pitchBand}
        />
      ))}
      {/* Lignes du terrain en blanc */}
      <g stroke={T.pitchLineStrong} strokeWidth="2" fill="none">
        {lines}
      </g>
    </g>
  )
}

function renderLinesLandscape(w, h) {
  // Surfaces de réparation : 16,5m sur 105m × 40,3m sur 68m
  // Approx : penaltyW = 165 (16.5%×1000), penaltyH = 405 (à scale)
  const penW = (16.5 / 105) * w
  const penH = (40.3 / 68) * h
  const penY = (h - penH) / 2
  const goalW = (5.5 / 105) * w
  const goalH = (18.32 / 68) * h
  const goalY = (h - goalH) / 2
  const penSpotX = (11 / 105) * w
  const cx = w / 2
  const cy = h / 2
  const centerR = (9.15 / 68) * h

  return (
    <>
      {/* Outer rect */}
      <rect x={1} y={1} width={w - 2} height={h - 2} />
      {/* Mid line */}
      <line x1={cx} y1={0} x2={cx} y2={h} />
      {/* Center circle + spot */}
      <circle cx={cx} cy={cy} r={centerR} />
      <circle cx={cx} cy={cy} r={2.5} fill="rgba(255,255,255,0.85)" />
      {/* Left penalty area */}
      <rect x={0} y={penY} width={penW} height={penH} />
      <rect x={0} y={goalY} width={goalW} height={goalH} />
      <circle cx={penSpotX} cy={cy} r={2.5} fill="rgba(255,255,255,0.85)" />
      {/* Right penalty area */}
      <rect x={w - penW} y={penY} width={penW} height={penH} />
      <rect x={w - goalW} y={goalY} width={goalW} height={goalH} />
      <circle cx={w - penSpotX} cy={cy} r={2.5} fill="rgba(255,255,255,0.85)" />
    </>
  )
}

function renderLinesPortrait(w, h) {
  // Symétrique : haut/bas au lieu de gauche/droite
  const penH = (16.5 / 105) * h
  const penW = (40.3 / 68) * w
  const penX = (w - penW) / 2
  const goalH = (5.5 / 105) * h
  const goalW = (18.32 / 68) * w
  const goalX = (w - goalW) / 2
  const penSpotY = (11 / 105) * h
  const cx = w / 2
  const cy = h / 2
  const centerR = (9.15 / 68) * w

  return (
    <>
      <rect x={1} y={1} width={w - 2} height={h - 2} />
      <line x1={0} y1={cy} x2={w} y2={cy} />
      <circle cx={cx} cy={cy} r={centerR} />
      <circle cx={cx} cy={cy} r={2.5} fill="rgba(255,255,255,0.85)" />
      {/* Top penalty area */}
      <rect x={penX} y={0} width={penW} height={penH} />
      <rect x={goalX} y={0} width={goalW} height={goalH} />
      <circle cx={cx} cy={penSpotY} r={2.5} fill="rgba(255,255,255,0.85)" />
      {/* Bottom penalty area */}
      <rect x={penX} y={h - penH} width={penW} height={penH} />
      <rect x={goalX} y={h - goalH} width={goalW} height={goalH} />
      <circle cx={cx} cy={h - penSpotY} r={2.5} fill="rgba(255,255,255,0.85)" />
    </>
  )
}

function ArrowMarkers() {
  // Trois markers d'arrow head, un par couleur
  return (
    <defs>
      {Object.entries(ARROW_STYLES).map(([type, s]) => (
        <marker
          key={type}
          id={`arrowhead-${type}`}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={s.color} />
        </marker>
      ))}
    </defs>
  )
}

function ArrowEl({ arrow, viewBox, selected, onSelect, readOnly }) {
  const style = ARROW_STYLES[arrow.type] || ARROW_STYLES.pass
  const fx = (arrow.from.x / 100) * viewBox.w
  const fy = (arrow.from.y / 100) * viewBox.h
  const tx = (arrow.to.x / 100) * viewBox.w
  const ty = (arrow.to.y / 100) * viewBox.h

  // Build path : straight line ou Bezier quadratique vers le haut
  let d
  if (arrow.curved) {
    // Control point perpendiculaire au midpoint, offset 18% du min dim
    const mx = (fx + tx) / 2
    const my = (fy + ty) / 2
    const dx = tx - fx
    const dy = ty - fy
    const len = Math.hypot(dx, dy) || 1
    const nx = -dy / len     // normale unitaire
    const ny = dx / len
    const minDim = Math.min(viewBox.w, viewBox.h)
    const offset = minDim * 0.12
    const cxp = mx + nx * offset
    const cyp = my + ny * offset
    d = `M ${fx} ${fy} Q ${cxp} ${cyp} ${tx} ${ty}`
  } else {
    d = `M ${fx} ${fy} L ${tx} ${ty}`
  }

  return (
    <g>
      {/* Hit area large pour faciliter le clic, transparent */}
      {!readOnly && (
        <path
          d={d}
          stroke="transparent"
          strokeWidth="14"
          fill="none"
          style={{ cursor: 'pointer' }}
          onPointerDown={(e) => { e.stopPropagation(); onSelect?.(arrow.id) }}
        />
      )}
      <path
        d={d}
        stroke={style.color}
        strokeWidth={selected ? style.width + 2 : style.width}
        strokeDasharray={style.dash}
        fill="none"
        markerEnd={`url(#arrowhead-${arrow.type})`}
        style={{
          filter: selected
            ? `drop-shadow(0 0 4px ${T.gold})`
            : `drop-shadow(0 1px 1px rgba(0,0,0,0.5))`,
          pointerEvents: 'none',
        }}
      />
    </g>
  )
}

function Token({ token, viewBox, selected, onPointerDown, readOnly }) {
  const cx = (token.x / 100) * viewBox.w
  const cy = (token.y / 100) * viewBox.h
  const minDim = Math.min(viewBox.w, viewBox.h)
  const r = (TOKEN_R_PCT / 100) * minDim
  const color = TEAM_COLOR[token.team] || T.gold

  return (
    <g
      style={{
        cursor: readOnly ? 'default' : 'grab',
        filter: selected
          ? `drop-shadow(0 0 6px ${T.gold})`
          : 'drop-shadow(0 2px 2px rgba(0,0,0,0.4))',
      }}
      onPointerDown={readOnly ? undefined : onPointerDown}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={color}
        stroke={selected ? '#fff' : T.dark}
        strokeWidth={selected ? 2.2 : 1.4}
      />
      {token.label && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={T.mono}
          fontWeight="700"
          fontSize={r * 1.05}
          fill={token.team === 'away' ? '#fff' : T.dark}
          pointerEvents="none"
        >
          {token.label}
        </text>
      )}
    </g>
  )
}

function Ball({ ball, viewBox, selected, onPointerDown, readOnly }) {
  const cx = (ball.x / 100) * viewBox.w
  const cy = (ball.y / 100) * viewBox.h
  const minDim = Math.min(viewBox.w, viewBox.h)
  const r = (BALL_R_PCT / 100) * minDim

  return (
    <g
      style={{
        cursor: readOnly ? 'default' : 'grab',
        filter: selected
          ? `drop-shadow(0 0 6px ${T.gold})`
          : 'drop-shadow(0 2px 2px rgba(0,0,0,0.45))',
      }}
      onPointerDown={readOnly ? undefined : onPointerDown}
    >
      <circle cx={cx} cy={cy} r={r} fill="#fff"
              stroke={selected ? T.gold : T.dark}
              strokeWidth={selected ? 2.2 : 1.4} />
      {/* Petite tâche noire stylisée pentagone */}
      <polygon
        points={(() => {
          const sides = 5
          const inner = r * 0.45
          const pts = []
          for (let i = 0; i < sides; i++) {
            const a = (Math.PI * 2 * i) / sides - Math.PI / 2
            pts.push(`${cx + Math.cos(a) * inner},${cy + Math.sin(a) * inner}`)
          }
          return pts.join(' ')
        })()}
        fill={T.dark}
      />
    </g>
  )
}

function ConeShape({ cone, viewBox, selected, onPointerDown, readOnly }) {
  const cx = (cone.x / 100) * viewBox.w
  const cy = (cone.y / 100) * viewBox.h
  const minDim = Math.min(viewBox.w, viewBox.h)
  const half = (CONE_HALF_PCT / 100) * minDim
  const path = `M ${cx} ${cy - half} L ${cx + half} ${cy + half * 0.85} L ${cx - half} ${cy + half * 0.85} z`

  return (
    <path
      d={path}
      fill="#ff8c00"
      stroke={selected ? T.gold : T.dark}
      strokeWidth={selected ? 2.2 : 1.4}
      style={{
        cursor: readOnly ? 'default' : 'grab',
        filter: selected
          ? `drop-shadow(0 0 6px ${T.gold})`
          : 'drop-shadow(0 2px 2px rgba(0,0,0,0.4))',
      }}
      onPointerDown={readOnly ? undefined : onPointerDown}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────
// Toolbar
// ─────────────────────────────────────────────────────────────────────

function ToolbarButton({ active, disabled, onClick, title, children }) {
  const bg = active ? T.gold : (disabled ? T.bgAlt : T.surface)
  const fg = active ? T.dark : (disabled ? T.muted : T.ink)
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: bg, color: fg, border: `2px solid ${T.dark}`,
        padding: '6px 10px',
        fontFamily: T.mono, fontSize: 10, letterSpacing: '.06em',
        textTransform: 'uppercase',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        userSelect: 'none',
      }}
    >
      {children}
    </button>
  )
}

function Toolbar({
  tool, setTool, arrowCurved, setArrowCurved,
  onUndo, onRedo, canUndo, canRedo, onReset, onDeleteSelected, hasSelection,
  orientation, onToggleOrientation,
}) {
  return (
    <div style={{
      display: 'flex', gap: 6, flexWrap: 'wrap',
      alignItems: 'center', marginBottom: 8,
      padding: '8px',
      background: T.bgAlt, border: `2px solid ${T.dark}`,
    }}>
      <ToolbarButton active={tool === TOOLS.SELECT} onClick={() => setTool(TOOLS.SELECT)} title="Sélection">
        <MousePointer2 size={12} /> Sélect.
      </ToolbarButton>

      <span style={{ width: 1, alignSelf: 'stretch', background: T.dark, opacity: 0.3 }} />

      <ToolbarButton active={tool === TOOLS.TOKEN_HOME} onClick={() => setTool(TOOLS.TOKEN_HOME)} title="Pion équipe">
        <span style={{ width: 12, height: 12, borderRadius: 6, background: T.gold, border: `1.5px solid ${T.dark}` }} /> Pion H
      </ToolbarButton>
      <ToolbarButton active={tool === TOOLS.TOKEN_AWAY} onClick={() => setTool(TOOLS.TOKEN_AWAY)} title="Pion adversaire">
        <span style={{ width: 12, height: 12, borderRadius: 6, background: T.red, border: `1.5px solid ${T.dark}` }} /> Pion A
      </ToolbarButton>
      <ToolbarButton active={tool === TOOLS.BALL} onClick={() => setTool(TOOLS.BALL)} title="Ballon">
        <Circle size={12} /> Ballon
      </ToolbarButton>
      <ToolbarButton active={tool === TOOLS.CONE} onClick={() => setTool(TOOLS.CONE)} title="Cône">
        <ConeIcon size={12} /> Cône
      </ToolbarButton>

      <span style={{ width: 1, alignSelf: 'stretch', background: T.dark, opacity: 0.3 }} />

      <ToolbarButton active={tool === TOOLS.ARROW_PASS} onClick={() => setTool(TOOLS.ARROW_PASS)} title="Passe">
        <ArrowRight size={12} /> Passe
      </ToolbarButton>
      <ToolbarButton active={tool === TOOLS.ARROW_RUN} onClick={() => setTool(TOOLS.ARROW_RUN)} title="Course">
        <ArrowRight size={12} /> Course
      </ToolbarButton>
      <ToolbarButton active={tool === TOOLS.ARROW_DRIBBLE} onClick={() => setTool(TOOLS.ARROW_DRIBBLE)} title="Dribble">
        <ArrowRight size={12} /> Dribble
      </ToolbarButton>

      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: T.mono, fontSize: 10, letterSpacing: '.06em',
        textTransform: 'uppercase', color: T.ink, marginLeft: 4,
      }}>
        <input
          type="checkbox"
          checked={arrowCurved}
          onChange={(e) => setArrowCurved(e.target.checked)}
          style={{ accentColor: T.gold }}
        />
        Courbe
      </label>

      <span style={{ width: 1, alignSelf: 'stretch', background: T.dark, opacity: 0.3 }} />

      <ToolbarButton onClick={onUndo} disabled={!canUndo} title="Annuler (Cmd+Z)">
        <Undo2 size={12} /> Annuler
      </ToolbarButton>
      <ToolbarButton onClick={onRedo} disabled={!canRedo} title="Refaire (Cmd+Shift+Z)">
        <Redo2 size={12} /> Refaire
      </ToolbarButton>
      <ToolbarButton onClick={onDeleteSelected} disabled={!hasSelection} title="Supprimer sélection (Suppr)">
        <Trash2 size={12} /> Suppr.
      </ToolbarButton>
      <ToolbarButton onClick={onReset} title="Réinitialiser le schéma">
        <RotateCcw size={12} /> Réinit
      </ToolbarButton>

      <span style={{ width: 1, alignSelf: 'stretch', background: T.dark, opacity: 0.3 }} />

      <ToolbarButton onClick={onToggleOrientation} title="Pivoter terrain">
        <RotateCw size={12} /> {orientation === 'landscape' ? 'Paysage' : 'Portrait'}
      </ToolbarButton>
    </div>
  )
}

function Legend() {
  const Item = ({ swatch, label }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
      textTransform: 'uppercase', color: T.ink2,
    }}>
      {swatch} {label}
    </span>
  )
  const dot = (color) => (
    <span style={{ width: 10, height: 10, borderRadius: 5, background: color, border: `1.4px solid ${T.dark}` }} />
  )
  const tri = (
    <span style={{
      width: 0, height: 0, marginRight: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: '10px solid #ff8c00',
    }} />
  )
  return (
    <div style={{
      display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8,
      padding: '8px 10px', background: T.bgAlt, border: `1.5px solid ${T.rule}`,
    }}>
      <Item swatch={dot(T.gold)} label="Équipe" />
      <Item swatch={dot(T.red)} label="Adversaire" />
      <Item swatch={dot('#fff')} label="Ballon" />
      <Item swatch={tri} label="Cône" />
      <Item
        swatch={<span style={{ width: 16, height: 2, background: '#fff' }} />}
        label="Passe"
      />
      <Item
        swatch={<span style={{ width: 16, height: 2, background: T.gold,
                                backgroundImage: `repeating-linear-gradient(90deg, ${T.gold} 0 4px, transparent 4px 7px)` }} />}
        label="Course"
      />
      <Item
        swatch={<span style={{ width: 16, height: 2,
                                backgroundImage: `repeating-linear-gradient(90deg, ${T.red} 0 1.5px, transparent 1.5px 4px)` }} />}
        label="Dribble"
      />
    </div>
  )
}

function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,9,8,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div style={{
        background: T.surface, maxWidth: 420, width: '100%',
        border: `2px solid ${T.dark}`, borderTop: `3px solid ${T.gold}`,
        boxShadow: `8px 8px 0 ${T.dark}`,
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1.5px solid ${T.rule}`,
                      display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={18} color={T.gold} />
          <div style={{ fontFamily: T.display, fontSize: 18, color: T.ink, textTransform: 'uppercase' }}>
            {title}
          </div>
        </div>
        <div style={{ padding: '16px 20px', fontFamily: T.mono, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>
          {message}
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1.5px solid ${T.rule}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: T.surface, border: `2px solid ${T.dark}`, color: T.ink,
              padding: '8px 14px', fontFamily: T.mono, fontSize: 10,
              letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer',
            }}>Annuler</button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              background: T.gold, border: `2px solid ${T.dark}`, color: T.dark,
              padding: '8px 14px', fontFamily: T.mono, fontSize: 10, fontWeight: 700,
              letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer',
            }}>Confirmer</button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────

export default function PitchEditor({
  initialSchema = null,
  initialOrientation = 'landscape',
  onChange = null,
  readOnly = false,
  showToolbar = true,
  height = null,
}) {
  const [schema, setSchemaState] = useState(() => normalizeSchema(initialSchema || emptySchema()))
  const [orientation, setOrientation] = useState(initialOrientation === 'portrait' ? 'portrait' : 'landscape')
  const [tool, setTool] = useState(TOOLS.SELECT)
  const [arrowCurved, setArrowCurved] = useState(false)
  const [selected, setSelected] = useState(null)        // {type:'token'|'arrow'|'cone'|'ball', id}
  const [pendingArrow, setPendingArrow] = useState(null)// {from:{x,y}}
  const [previewTo, setPreviewTo] = useState(null)      // {x,y} pour la preview de flèche
  const [resetConfirm, setResetConfirm] = useState(false)

  const svgRef = useRef(null)
  const draggingRef = useRef(null)                      // { type, id, pointerId, lastX, lastY, moved }
  const past = useRef([])
  const future = useRef([])
  const lastEmittedRef = useRef({ schema: null, orientation: null })

  const viewBox = orientation === 'portrait' ? FIELD_PORTRAIT : FIELD_LANDSCAPE

  // ── Effets ───
  // Pas de sync prop -> state au-dela du mount : composant uncontrolled
  // sur initialSchema/initialOrientation. Le parent qui veut forcer un
  // reset doit changer le `key` du composant pour declencher un remount
  // (cf. ExerciseForm qui utilise key={initial?.id || 'new'}). Cela evite
  // une boucle de re-render `setState -> onChange -> parent -> nouveau
  // initialSchema ref -> reset effect -> setState -> ...` qui faisait
  // clignoter selection/pending arrow pendant les drags.

  // Emit onChange uniquement quand schema OU orientation diffèrent réellement
  useEffect(() => {
    if (!onChange) return
    if (
      lastEmittedRef.current.schema === schema
      && lastEmittedRef.current.orientation === orientation
    ) return
    lastEmittedRef.current = { schema, orientation }
    onChange(schema, orientation)
  }, [schema, orientation, onChange])

  // Keyboard shortcuts (undo/redo, delete)
  useEffect(() => {
    if (readOnly) return
    const onKey = (e) => {
      // Ne pas intercepter si l'user tape dans un input
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
      const isMeta = e.metaKey || e.ctrlKey
      if (isMeta && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault(); undo()
      } else if (isMeta && (e.key.toLowerCase() === 'z' && e.shiftKey || e.key.toLowerCase() === 'y')) {
        e.preventDefault(); redo()
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected) {
        e.preventDefault(); deleteSelected()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setSelected(null)
        setPendingArrow(null)
        setPreviewTo(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, selected, schema])

  // ── History helpers ───
  const pushHistory = useCallback((next) => {
    past.current = [...past.current, schema].slice(-MAX_HISTORY)
    future.current = []
    setSchemaState(next)
  }, [schema])

  const undo = useCallback(() => {
    if (past.current.length === 0) return
    const prev = past.current[past.current.length - 1]
    past.current = past.current.slice(0, -1)
    future.current = [...future.current, schema].slice(-MAX_HISTORY)
    setSchemaState(prev)
    setSelected(null)
    setPendingArrow(null)
  }, [schema])

  const redo = useCallback(() => {
    if (future.current.length === 0) return
    const next = future.current[future.current.length - 1]
    future.current = future.current.slice(0, -1)
    past.current = [...past.current, schema].slice(-MAX_HISTORY)
    setSchemaState(next)
    setSelected(null)
    setPendingArrow(null)
  }, [schema])

  // ── Mutations ───
  const addToken = useCallback((x, y, team) => {
    if (schema.tokens.filter(t => t.type !== 'ball').length >= MAX_TOKENS) return
    const next = {
      ...schema,
      tokens: [...schema.tokens, { id: newId(), x, y, team, label: '' }],
    }
    pushHistory(next)
  }, [schema, pushHistory])

  const addBall = useCallback((x, y) => {
    // Un seul ballon : retire l'existant
    const filtered = schema.tokens.filter(t => t.type !== 'ball')
    const next = {
      ...schema,
      tokens: [...filtered, { id: newId(), x, y, type: 'ball' }],
    }
    pushHistory(next)
  }, [schema, pushHistory])

  const addCone = useCallback((x, y) => {
    const next = { ...schema, cones: [...schema.cones, { id: newId(), x, y }] }
    pushHistory(next)
  }, [schema, pushHistory])

  const finishArrow = useCallback((from, to, type, curved) => {
    if (distPct(from, to) < MIN_ARROW_PX) return
    const next = {
      ...schema,
      arrows: [...schema.arrows, {
        id: newId(),
        from: { x: from.x, y: from.y },
        to: { x: to.x, y: to.y },
        type,
        curved: !!curved,
      }],
    }
    pushHistory(next)
  }, [schema, pushHistory])

  const moveItem = useCallback((kind, id, dx, dy) => {
    // dx,dy en pourcent
    if (kind === 'token' || kind === 'ball') {
      const next = {
        ...schema,
        tokens: schema.tokens.map(t => t.id === id
          ? { ...t, x: clamp(t.x + dx, 1, 99), y: clamp(t.y + dy, 1, 99) }
          : t),
      }
      setSchemaState(next)
    } else if (kind === 'cone') {
      const next = {
        ...schema,
        cones: schema.cones.map(c => c.id === id
          ? { ...c, x: clamp(c.x + dx, 1, 99), y: clamp(c.y + dy, 1, 99) }
          : c),
      }
      setSchemaState(next)
    }
  }, [schema])

  const deleteSelected = useCallback(() => {
    if (!selected) return
    let next
    if (selected.type === 'arrow') {
      next = { ...schema, arrows: schema.arrows.filter(a => a.id !== selected.id) }
    } else if (selected.type === 'cone') {
      next = { ...schema, cones: schema.cones.filter(c => c.id !== selected.id) }
    } else { // token ou ball
      next = { ...schema, tokens: schema.tokens.filter(t => t.id !== selected.id) }
    }
    pushHistory(next)
    setSelected(null)
  }, [schema, selected, pushHistory])

  const toggleArrowCurved = useCallback((arrowId) => {
    const next = {
      ...schema,
      arrows: schema.arrows.map(a => a.id === arrowId ? { ...a, curved: !a.curved } : a),
    }
    pushHistory(next)
  }, [schema, pushHistory])

  // ── Pointer handlers ───
  const handleSvgPointerDown = useCallback((e) => {
    if (readOnly) return
    if (e.button !== undefined && e.button !== 0) return // ignore right-click etc
    const pos = clientToPct(svgRef.current, e.clientX, e.clientY)
    if (!pos) return

    if (tool === TOOLS.TOKEN_HOME) { addToken(pos.x, pos.y, 'home'); return }
    if (tool === TOOLS.TOKEN_AWAY) { addToken(pos.x, pos.y, 'away'); return }
    if (tool === TOOLS.BALL)       { addBall(pos.x, pos.y); return }
    if (tool === TOOLS.CONE)       { addCone(pos.x, pos.y); return }

    const isArrowTool = tool === TOOLS.ARROW_PASS || tool === TOOLS.ARROW_RUN || tool === TOOLS.ARROW_DRIBBLE
    if (isArrowTool) {
      if (!pendingArrow) {
        setPendingArrow({ from: pos })
        setPreviewTo(pos)
      } else {
        const arrowType = tool.replace('arrow-', '')
        finishArrow(pendingArrow.from, pos, arrowType, arrowCurved)
        setPendingArrow(null)
        setPreviewTo(null)
      }
      return
    }

    // Sélection : clic terrain vide => déselection
    if (tool === TOOLS.SELECT) {
      setSelected(null)
    }
  }, [tool, readOnly, pendingArrow, addToken, addBall, addCone, finishArrow, arrowCurved])

  const handleSvgPointerMove = useCallback((e) => {
    if (readOnly) return
    // Drag d'un item
    if (draggingRef.current) {
      const drag = draggingRef.current
      const pos = clientToPct(svgRef.current, e.clientX, e.clientY)
      if (!pos) return
      const dx = pos.x - drag.lastX
      const dy = pos.y - drag.lastY
      drag.lastX = pos.x
      drag.lastY = pos.y
      drag.moved = drag.moved || (Math.abs(dx) + Math.abs(dy) > 0.05)
      moveItem(drag.kind, drag.id, dx, dy)
      return
    }
    // Preview de flèche
    if (pendingArrow) {
      const pos = clientToPct(svgRef.current, e.clientX, e.clientY)
      if (pos) setPreviewTo(pos)
    }
  }, [readOnly, pendingArrow, moveItem])

  const handleSvgPointerUp = useCallback((e) => {
    if (readOnly) return
    if (draggingRef.current) {
      const drag = draggingRef.current
      try { e.target?.releasePointerCapture?.(drag.pointerId) } catch {}
      // Si déplacement réel, push history
      if (drag.moved) {
        past.current = [...past.current, drag.snapshotBefore].slice(-MAX_HISTORY)
        future.current = []
      }
      draggingRef.current = null
    }
  }, [readOnly])

  // Handler pour démarrer un drag depuis un item
  const beginDrag = useCallback((kind, id) => (e) => {
    if (readOnly) return
    if (tool !== TOOLS.SELECT && tool !== TOOLS.TOKEN_HOME && tool !== TOOLS.TOKEN_AWAY
        && tool !== TOOLS.BALL && tool !== TOOLS.CONE) {
      // Si outil flèche actif, le clic sur un pion sert de point de départ d'arrow
      e.stopPropagation()
      const pos = clientToPct(svgRef.current, e.clientX, e.clientY)
      if (!pos) return
      const isArrowTool = tool === TOOLS.ARROW_PASS || tool === TOOLS.ARROW_RUN || tool === TOOLS.ARROW_DRIBBLE
      if (isArrowTool) {
        if (!pendingArrow) {
          setPendingArrow({ from: pos })
          setPreviewTo(pos)
        } else {
          const arrowType = tool.replace('arrow-', '')
          finishArrow(pendingArrow.from, pos, arrowType, arrowCurved)
          setPendingArrow(null)
          setPreviewTo(null)
        }
      }
      return
    }

    e.stopPropagation()
    const pos = clientToPct(svgRef.current, e.clientX, e.clientY)
    if (!pos) return
    setSelected({ type: kind, id })
    try { e.target.setPointerCapture(e.pointerId) } catch {}
    draggingRef.current = {
      kind, id, pointerId: e.pointerId,
      lastX: pos.x, lastY: pos.y, moved: false,
      snapshotBefore: schema, // pour history en fin de drag
    }
  }, [tool, readOnly, pendingArrow, arrowCurved, finishArrow, schema])

  const handleSelectArrow = useCallback((id) => {
    if (readOnly) return
    if (tool !== TOOLS.SELECT) return
    setSelected({ type: 'arrow', id })
  }, [tool, readOnly])

  // ── Sélection rendering helpers ───
  const isSel = useCallback((kind, id) => selected?.type === kind && selected?.id === id, [selected])

  const ball = useMemo(() => schema.tokens.find(t => t.type === 'ball'), [schema.tokens])
  const players = useMemo(() => schema.tokens.filter(t => t.type !== 'ball'), [schema.tokens])

  // ── Render ───
  const containerStyle = {
    width: '100%',
    maxWidth: orientation === 'landscape' ? 1100 : 720,
    margin: '0 auto',
  }

  const svgStyle = {
    width: '100%',
    height: height || 'auto',
    background: T.dark,
    border: `2px solid ${T.dark}`,
    boxShadow: `6px 6px 0 ${T.dark}`,
    touchAction: 'none',
    userSelect: 'none',
    display: 'block',
  }

  const cursor = (() => {
    if (readOnly) return 'default'
    if (tool === TOOLS.SELECT) return 'default'
    if (tool === TOOLS.TOKEN_HOME || tool === TOOLS.TOKEN_AWAY
        || tool === TOOLS.BALL || tool === TOOLS.CONE) return 'crosshair'
    return 'crosshair' // arrow tools
  })()

  return (
    <div style={containerStyle}>
      {showToolbar && !readOnly && (
        <Toolbar
          tool={tool} setTool={(t) => { setTool(t); setPendingArrow(null); setPreviewTo(null) }}
          arrowCurved={arrowCurved} setArrowCurved={(v) => {
            setArrowCurved(v)
            // Si une flèche est sélectionnée, on toggle sa courbure aussi
            if (selected?.type === 'arrow') toggleArrowCurved(selected.id)
          }}
          onUndo={undo} onRedo={redo}
          canUndo={past.current.length > 0} canRedo={future.current.length > 0}
          onReset={() => setResetConfirm(true)}
          onDeleteSelected={deleteSelected}
          hasSelection={!!selected}
          orientation={orientation}
          onToggleOrientation={() => setOrientation(o => o === 'landscape' ? 'portrait' : 'landscape')}
        />
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewBox.w} ${viewBox.h}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ ...svgStyle, cursor }}
        onPointerDown={handleSvgPointerDown}
        onPointerMove={handleSvgPointerMove}
        onPointerUp={handleSvgPointerUp}
        onPointerCancel={handleSvgPointerUp}
      >
        <ArrowMarkers />
        <Field orientation={orientation} />

        {/* Cônes (sous les flèches) */}
        {schema.cones.map(c => (
          <ConeShape
            key={c.id}
            cone={c}
            viewBox={viewBox}
            selected={isSel('cone', c.id)}
            onPointerDown={beginDrag('cone', c.id)}
            readOnly={readOnly}
          />
        ))}

        {/* Flèches existantes */}
        {schema.arrows.map(a => (
          <ArrowEl
            key={a.id}
            arrow={a}
            viewBox={viewBox}
            selected={isSel('arrow', a.id)}
            onSelect={handleSelectArrow}
            readOnly={readOnly}
          />
        ))}

        {/* Preview de flèche en cours */}
        {pendingArrow && previewTo && (() => {
          const arrowType = tool.replace('arrow-', '')
          return (
            <ArrowEl
              arrow={{
                id: 'preview',
                from: pendingArrow.from,
                to: previewTo,
                type: arrowType,
                curved: arrowCurved,
              }}
              viewBox={viewBox}
              selected={false}
              readOnly
            />
          )
        })()}

        {/* Joueurs */}
        {players.map(t => (
          <Token
            key={t.id}
            token={t}
            viewBox={viewBox}
            selected={isSel('token', t.id)}
            onPointerDown={beginDrag('token', t.id)}
            readOnly={readOnly}
          />
        ))}

        {/* Ballon (au-dessus des joueurs) */}
        {ball && (
          <Ball
            ball={ball}
            viewBox={viewBox}
            selected={isSel('ball', ball.id)}
            onPointerDown={beginDrag('ball', ball.id)}
            readOnly={readOnly}
          />
        )}
      </svg>

      {showToolbar && !readOnly && <Legend />}

      {resetConfirm && (
        <ConfirmModal
          title="Réinitialiser le schéma ?"
          message="Toutes les pièces et flèches seront supprimées. Cette action peut être annulée avec Cmd+Z."
          onCancel={() => setResetConfirm(false)}
          onConfirm={() => {
            pushHistory(emptySchema())
            setSelected(null)
            setPendingArrow(null)
            setPreviewTo(null)
            setResetConfirm(false)
          }}
        />
      )}
    </div>
  )
}
